import { compact, get, set, cloneDeep } from 'lodash';
import messages from './messages';
import getSymbolFromCurrency from 'currency-symbol-map';

/**
 * Return list of headers (columns) to be displayed with their title, key accessor,
 * and formatting function
 * Unguaranteed data is available both with the guaranteed data (for the table) or as extra columns (for the export)
 * @param  {String} title - title of current segment
 * @param  {String} currency
 * @param  {Object} intl  - from react-intl
 * @param  {Boolean} addNonGuaranteedColumns
 * @return {Object[]} an array with an object for each key to be displayed
 */
const getHeadersFormatters = (title, currency, intl, addNonGuaranteedColumns = false, hideOccupancy = false) => {
    const formatRooms = (value) => intl.formatNumber(value);
    const makeRevenueFormatter = (precision) => (value) => {
        const currencySymbol = getSymbolFromCurrency(currency);
        const numericalValue = intl.formatNumber(value / 100, {
            minimumFractionDigits: precision,
            maximumFractionDigits: precision,
        });
        return `${currencySymbol}${numericalValue}`;
    };
    const formatPercentage = (value) =>
        intl.formatNumber(value, {
            style: 'percent',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });

    return compact([
        {
            label: title,
            key: 'label',
            formatter: (value) => value,
        },
        {
            label: intl.formatMessage(messages.table.roomsHeader),
            key: 'rooms.guaranteed',
            nonGuaranteedValueKey: 'rooms.nonGuaranteed',
            formatter: formatRooms,
        },
        addNonGuaranteedColumns && {
            label: intl.formatMessage(messages.table.nonGuaranteedRoomsHeader),
            key: 'rooms.nonGuaranteed',
            formatter: formatRooms,
        },
        {
            label: intl.formatMessage(messages.table.revenueHeader),
            key: 'revenue.accommodation.guaranteed',
            nonGuaranteedValueKey: 'revenue.accommodation.nonGuaranteed',
            formatter: makeRevenueFormatter(0),
        },
        addNonGuaranteedColumns && {
            label: intl.formatMessage(messages.table.nonGuaranteedRevenueHeader),
            key: 'revenue.accommodation.nonGuaranteed',
            formatter: makeRevenueFormatter(0),
        },
        !hideOccupancy && {
            label: intl.formatMessage(messages.table.adrHeader),
            key: 'adr.guaranteed',
            nonGuaranteedValueKey: 'adr.nonGuaranteed',
            formatter: makeRevenueFormatter(2),
        },
        !hideOccupancy &&
            addNonGuaranteedColumns && {
                label: intl.formatMessage(messages.table.nonGuaranteedAdrHeader),
                key: 'adr.nonGuaranteed',
                formatter: makeRevenueFormatter(2),
            },
        !hideOccupancy && {
            label: intl.formatMessage(messages.table.revParHeader),
            key: 'revPar.guaranteed',
            nonGuaranteedValueKey: 'revPar.nonGuaranteed',
            formatter: makeRevenueFormatter(2),
        },
        !hideOccupancy &&
            addNonGuaranteedColumns && {
                label: intl.formatMessage(messages.table.nonGuaranteedRevParHeader),
                key: 'revPar.nonGuaranteed',
                formatter: makeRevenueFormatter(2),
            },
        !hideOccupancy && {
            label: intl.formatMessage(messages.table.occupancyHeader),
            key: 'occupancy.guaranteed',
            nonGuaranteedValueKey: 'occupancy.nonGuaranteed',
            formatter: formatPercentage,
        },
        !hideOccupancy &&
            addNonGuaranteedColumns && {
                label: intl.formatMessage(messages.table.nonGuaranteedOccupancyHeader),
                key: 'occupancy.nonGuaranteed',
                formatter: formatPercentage,
            },
    ]);
};

/**
 * Applies a list of formatters (from #getHeadersFormatters above) to every row of data
 * @param  {Object[]} data
 * @param  {Object[]} formatters - returned by #getHeadersFormatters
 * @return {Object[]} identical array of data with every key in "formatters" formatted
 */
const applyCSVFormatters = (data, formatters) =>
    cloneDeep(data).map((row) => {
        formatters.forEach(({ key, formatter }) => {
            set(row, key, formatter(get(row, key), true));
        });
        return row;
    });

export { getHeadersFormatters, applyCSVFormatters };
